import React, { useEffect, useState, useRef, Suspense } from 'react'
import TestTag from '../common/testclinic.tag'
import LanguageTag from '../common/language.tag'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  getPatientList,
  rowSelect,
  getReport,
  getStats,
  setPatientPassword,
  setPatient,
  getPatientNots,
  setPatientScheduleView,
} from '../../store/action/patient.action'
import { getSurgeonList } from '../../store/action/surgeon.action'
import { getFeedbackForms } from '../../store/action/feedback.action'
import { Table, Avatar, Row, Col, message, Modal, Tooltip, Button } from 'antd'
import {
  DownloadOutlined,
  FilePdfFilled,
  MailFilled,
  MessageOutlined,
} from '@ant-design/icons'
import util from '../../util/util'
import {
  matchesScheduleView,
  matchesPatientSearch,
} from '../../util/patientlistfilters'
import PatientPie from './patient.pie'
import '../../css/patient.scss'
import PatientMail from './patient.mail'
import { useHistory } from 'react-router'
import { constants } from '../../constants'
import PatientBar from './patient.bar'
import PatientMeasurementTable from './patient.measurement.table'
import moment from 'moment'
import PatientMeasurement from './patient.measurement'
import { messageGroupBy } from '../../store/action/actioncases.action'
import PatientActiveModal from './forms/patient.active.modal'
import CaseModal from './case.modal'

const { confirm } = Modal

const PatientTable = (props) => {
  var history = useHistory()
  const [sendmail, setSendmail] = useState('')
  const [caseOpen, setCaseOpen] = useState(false)
  const [singleRecord,setSingleRecord] = useState({})
  const [companyUser, setCompanyuser] = useState(
    props.role == constants.ROLE.COMPANY_HEALTHCOACH ||
      props.role == constants.ROLE.COMPANY_ADMIN
  )

  // Page index for the client-side pagination (search + paging happen in
  // memory over the whole list, so the table never re-queries the server for
  // navigation).
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  useEffect(() => {
    // Load the clinic's surgeon list so the filter dropdown (and surgeon-name
    // search) has data even when the user lands here without visiting Messages
    // first. Falls back to the token's customer id when the profile customer
    // hasn't loaded yet.
    props.fetchSurgeon(props.customer?._id || props.decoded?.customer)
    props.fetchFeedbackForms(props.customer?._id)
    // Load the ENTIRE patient list up front so the search box can filter
    // across everyone in the clinic (not just one server page). Pagination
    // and sorting are then handled client-side.
    fetchPatientList({ current: 1, pageSize: 100000 }, props.selectedPatients, {
      order: 'ascend',
      field: 'surgery',
    })
    props.getStats(props.customer?._id)
    props.messageGroupBy(null)
  }, [])

  // Reset to the first page whenever the search term or schedule view changes
  // so the user always sees matches from the top.
  useEffect(() => {
    setPage(1)
  }, [props.search, props.scheduleView])

  // Keep the Login Status column live without a manual browser refresh: silently
  // re-pull the list on an interval. A ref holds the latest fetch closure so the
  // poll always uses the current filters/search, not a stale mount-time copy.
  const silentRefreshRef = useRef(() => {})
  useEffect(() => {
    // Fire once shortly after mount, then poll — so a stale status corrects
    // quickly instead of waiting a full interval for the first refresh.
    const kick = setTimeout(() => silentRefreshRef.current(), 3000)
    const id = setInterval(() => silentRefreshRef.current(), 15000)
    return () => {
      clearTimeout(kick)
      clearInterval(id)
    }
  }, [])

  var fetchPatientList = (pagination, filters, sorter, silent = false) => {
    props.fetchPatient(
      {
        customerid: props.customer?._id,
        metadata: pagination,
        filters: {
          type: 'active', // Default to active patients
          ...props.filters,
          patients: history.location.state?.patientids,
          surgeon:
            props.decoded?.role == constants.ROLE.SURGEON
              ? [props.user?.surgeon]
              : props.filters?.surgeon,
        },
        sorter: sorter?.order
          ? { field: sorter.columnKey || sorter.field, order: sorter.order }
          : sorter,
      },
      props.selectedPatients,
      silent
    )
  }

  // Refresh with the same full-list params the mount load uses; silent so the
  // table spinner never flashes. Reassigned every render to capture live props.
  silentRefreshRef.current = () =>
    fetchPatientList(
      { current: 1, pageSize: 100000 },
      props.selectedPatients,
      { order: 'ascend', field: 'surgery' },
      true
    )

  var formatdata = (data) => {
    data && data.forEach((e, i) => (e.key = e._id))
    return data
  }

  // Both predicates now live in util/patientlistfilters so the Surgery Date
  // tab counts (patient.filter.js) and this table cannot disagree about who is
  // scheduled or who matches a search. See the note in that file: the header
  // counts and this list disagreeing is what made an empty table look like
  // lost patients on 2026-10-28.
  var matchesSchedule = (p) =>
    matchesScheduleView(p, props.scheduleView, props.filters)
  var matchesSearch = (p, term) =>
    matchesPatientSearch(p, term, props.surgeon)

  var allPatients = formatdata(props.patients) || []
  var dataSource = allPatients
    .filter((p) => matchesSchedule(p))
    .filter((p) => matchesSearch(p, props.search))

  // Patients the CURRENT search matches but the Surgery Date tab is hiding.
  // Drives the empty-state message below. Computed from the same two
  // predicates as dataSource, so the number it quotes is exactly what clicking
  // through would show.
  var searchMatched = allPatients.filter((p) =>
    matchesSearch(p, props.search)
  )
  var hiddenByScheduleView =
    props.filters?.type === 'inactive' ? 0 : searchMatched.length - dataSource.length
  var pagination = {
    current: page,
    pageSize: pageSize,
    total: dataSource.length,
    pageSizeOptions: [5, 10, 20, 50, 100],
    showSizeChanger: true,
    onChange: (p, ps) => {
      setPage(p)
      setPageSize(ps)
    },
    onShowSizeChange: (p, ps) => {
      setPage(1)
      setPageSize(ps)
    },
  }

  var handleRowClick = async (record) => {
    await props.setPatient({})
    history.push({ pathname: 'patient/' + record._id, state: record })
  }

  var handleRow = (record, rowIndex) => {
    return { onClick: () => handleRowClick(record) }
  }

  const rowSelection = {
    selectedRowKeys: props.selectedPatients?.key
      ? props.selectedPatients.key
      : [],
    onChange: (key, row) =>
      props.onRowSelect({
        key,
        row: util.filterRows(row, key, props.selectedPatients),
      }),
  }

  // Login Status column — a colored dot + label:
  //   green  Active     = active session AND last activity <= 10 min
  //   yellow Away       = active session AND last activity > 10 min
  //   red    Logged Out = no active session
  const PRESENCE = {
    active: { color: '#52c41a', label: 'Active' },
    away: { color: '#faad14', label: 'Away' },
    loggedout: { color: '#f5222d', label: 'Logged Out' },
  }

  // A last success older than this reads as overdue. Measured from the last
  // SUCCESS, not the last attempt: someone who failed yesterday but last
  // succeeded a month ago is overdue, and dating the rule off the failure would
  // hide exactly that.
  var CALIBRATION_STALE_DAYS = 8

  // Same L / R treatment the patient app's Settings badge uses
  // (TracPatchActions), so a clinician reading over a patient's shoulder sees
  // the same letter and colour on both screens.
  var LEG_SIDE = {
    left:  { letter: 'L', color: '#52c41a' },
    right: { letter: 'R', color: '#f5222d' },
  }

  var calibLegBadge = (legSide) => {
    var meta = LEG_SIDE[legSide]
    if (!meta) return null
    return (
      <span
        title={legSide === 'left' ? 'Left leg sensor' : 'Right leg sensor'}
        style={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0.5,
          color: meta.color,
        }}
      >
        {meta.letter}
      </span>
    )
  }

  var calibDot = (color) => (
    <span
      style={{
        flex: '0 0 auto',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        boxShadow: '0 0 0 2px ' + color + '40',
      }}
    />
  )

  // Calibration column.
  //
  // ⚠️ There is no longer a "no TracPatch assigned" state. It used to render
  // "—" whenever the patient had no tracpatchassignments row — which was every
  // patient who paired a device without the admin shipping flow, i.e. in
  // practice everyone. A patient who has not calibrated now reads "Never",
  // which is the thing worth chasing either way.
  //
  // Two INDEPENDENT signals, deliberately not collapsed into one:
  //   • staleness — last SUCCESS older than CALIBRATION_STALE_DAYS → red date
  //   • last attempt FAILED → flagged, whatever the success history says
  // A patient can be both. Showing only one would hide the other.
  var renderCalibration = (obj) => {
    var calib = obj.tracpatchCalibration
    var lastSuccess = calib && calib.calibratedAt
    var failed = !!(calib && calib.lastOutcome === 'failure')

    if (!lastSuccess) {
      return (
        <Tooltip
          title={
            failed
              ? 'Last attempt FAILED ' +
                moment(calib.lastAttemptAt).fromNow() +
                ' — never calibrated successfully'
              : 'Never calibrated'
          }
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {calibDot('#f5222d')}
            {failed ? 'Failed' : 'Never'}
            {/* The device reports its leg side independently of calibration, so
                this is often known even for a patient who has never calibrated
                — and it is useful precisely then. */}
            {calibLegBadge(calib && calib.legSide)}
          </span>
        </Tooltip>
      )
    }

    var stale = moment().diff(moment(lastSuccess), 'days') > CALIBRATION_STALE_DAYS
    var legText = calib && calib.legSide
      ? ' (' + (calib.legSide === 'left' ? 'left' : 'right') + ' leg)'
      : ''
    var tip =
      'Last calibrated ' + moment(lastSuccess).format('M/D/YYYY h:mm A') + legText +
      ', ' + moment(lastSuccess).fromNow() +
      (stale ? ' — over ' + CALIBRATION_STALE_DAYS + ' days ago' : '') +
      (failed ? '. Last attempt FAILED ' + moment(calib.lastAttemptAt).fromNow() : '')

    // Date and time on two lines rather than one: the column is narrow, and the
    // date is what gets scanned down the list — the time only matters once a
    // row has already caught someone's eye. The staleness colour goes on the
    // DATE only, so a red row stays readable.
    return (
      <Tooltip title={tip}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {failed ? calibDot('#faad14') : null}
          <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
            <span style={stale ? { color: '#f5222d' } : undefined}>
              {moment(lastSuccess).format('M/D/YYYY')}
            </span>
            {/* Same colour as the date, including the staleness red — the time
                is part of the same fact, not secondary metadata. Only the size
                differs, to keep the row's visual weight on the date. */}
            <span style={stale ? { fontSize: 11, color: '#f5222d' } : { fontSize: 11 }}>
              {moment(lastSuccess).format('h:mm A')}
            </span>
          </span>
          {calibLegBadge(calib && calib.legSide)}
        </span>
      </Tooltip>
    )
  }

  var renderLoginStatus = (obj) => {
    var meta = PRESENCE[obj.loginStatus] || PRESENCE.loggedout
    var seen = obj.lastActiveAt
      ? 'Last active ' + moment(obj.lastActiveAt).fromNow()
      : 'Never active'
    return (
      <Tooltip title={seen}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              flex: '0 0 auto',
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: meta.color,
              boxShadow: '0 0 0 2px ' + meta.color + '40',
            }}
          />
          {meta.label}
        </span>
      </Tooltip>
    )
  }

  var renderName = (name, obj) => {
    return (
      <Row className={'nameRow'}>
        <span>
          {_.upperFirst(obj.firstname) + ' ' + _.upperFirst(obj.lastname)}
        </span>
        {/* Renders nothing unless the flag is set, so it costs an unflagged
            row no space at all. */}
        <TestTag isTest={obj?.istestpatient} />
        {/* Spanish only: an "EN" badge on nearly every row would be noise.
            See component/common/language.tag.js. */}
        <LanguageTag language={obj?.language} />
      </Row>
    )
  }

  var rendersurgeryName = (surgery) => {
    return (
      <Row className={'nameRow'}>
        <span style={{ width: '100%', textAlign: 'center' }}>
          {_.upperFirst(surgery?.type)}
        </span>
      </Row>
    )
  }

  // Helper function to check if a specific surgery type is surgical
  const isSurgicalType = (surgeryTypeName) => {
    if (!props.customer?.surgerytypes || !surgeryTypeName) return true; // Default to true if no data

    const matchingSurgeryType = props.customer.surgerytypes.find(
      st => st.name?.toLowerCase() === surgeryTypeName.toLowerCase()
    );

    return matchingSurgeryType ? matchingSurgeryType.issurgical : true;
  }

  // Helper function to check if clinic has ANY surgical types
  const hasAnySurgicalTypes = () => {
    if (!props.customer?.surgerytypes || props.customer.surgerytypes.length === 0) return true;
    return props.customer.surgerytypes.some(st => st.issurgical === true);
  }

  var rendersurgery = (surgery) => {
    const isSurgical = isSurgicalType(surgery?.type);
    const daysDiff = moment().startOf('day').diff(moment(surgery?.date).startOf('day'), 'days');
    const isPostDate = daysDiff >= 0;
    const daysCount = Math.abs(daysDiff);

    return (
      <Row align='bottom'>
        <Col style={{ width: '100%' }}>
          <Row justify='center'>
            <Col>{moment(surgery?.date).format('MM/DD/YYYY')}</Col>
          </Row>
          <Row style={{ justifyContent: 'center', fontSize: '.8rem' }}>
            <Col>
              {isSurgical ? (
                <>
                  <span style={{ color: '#F86D70', fontWeight: 600 }}>
                    {isPostDate ? constants.SURGERYTIME.Post : constants.SURGERYTIME.Pre}{' '}
                  </span>
                  <span>{daysCount + ' days'}</span>
                </>
              ) : (
                <span style={{ color: '#000000' }}>
                  {isPostDate ? '+' : '-'}{daysCount} days
                </span>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }

  var renderScore = (scores) => {
    let uniqueforms = _.uniqBy(scores, 'formid')
    return uniqueforms.map((e, i) => {
      return e.score ? (
        <Row key={i + 1} style={{ textAlign: 'left' }}>
          <Col flex={'145px'}>
            {util.getObj(props.forms, e.formid)?.shortname}
          </Col>
          <Col flex={'30px'}>{' : ' + e.score}</Col>
        </Row>
      ) : null
    })
  }
    const isSameSurgeon=(record)=>{
      if(!props.user?.surgeon)return true; //Admin
    return props.user?.surgeon==record?.surgeon?._id
    }
  var renderActions = (record) => {
    return (
      <Row justify={'center'}>
        {/* <PatientActiveModal _id={record._id}  handlePatientList={handlePatientList}/> */}
        {!companyUser && (
          <DownloadOutlined
            id={'patientreportdownload'}
            title='Download report'
            className={'actionIcon downIcon'}
            onClick={(e) => {
              e.stopPropagation()
              props.getReport(record.key)
            }}
          />
        )}
        {/* <MailFilled
          id={'sendpatientmail'}
          title='Send email'
          className={'actionIcon downIcon'}
          onClick={(e) => {
            e.stopPropagation()
            setSendmail(record)
          }}
        /> */}
        {/* Clinic ("Surgeon Message") conversation icon disabled for now:
            clinics generally turn off in-app messaging and patient messages
            should come from MoveAI instead (see [Send Message] below).
            Functionality is kept intact — uncomment to restore.
           <MessageOutlined
          id={'sendpatientmail'}
          title='Start conversation'
          className={`actionIcon downIcon ${!isSameSurgeon(record) ? 'disabledIcon' : ''}`}
          disabled ={!isSameSurgeon(record)}
          onClick={(e) => {
           e.stopPropagation()
          if (!isSameSurgeon(record)) return;
            setCaseOpen(true)
            setSingleRecord(record)
          }}
        /> */}
        <div className={'patientActionCol'}>
          {companyUser && (
            <Button
              id={'sendmoveaimessage'}
              className={'patientActionBtn'}
              title={`Send message from ${constants.MOVEAI_SUPPORT_NAME}`}
              onClick={(e) => {
                e.stopPropagation()
                setCaseOpen(true)
                setSingleRecord(record)
              }}
            >
              Send Message
            </Button>
          )}
          <Button
            id={'resetpatientpassword'}
            className={'patientActionBtn'}
            title='Reset password'
            onClick={(e) => {
              e.stopPropagation()
              showConfirm(record)
            }}
          >
            Reset Password
          </Button>
        </div>
      </Row>
    )
  }
 var handleSuccess = (data) => {
    props.getNotifications(props.patient._id)
  }
  var renderSurgeon = (provider) => {
    // return util.getObj(props.surgeon, obj?.surgeon)?.name
    return provider?.firstname
  }

  function showConfirm(obj) {
    confirm({
      title: 'Do you want to reset patient password?',
      async onOk() {
        try {
          await handleResetPassword(obj)
        } catch (e) {
          return message.error(e?.message | ' errors!')
        }
      },
      onCancel() {},
    })
  }

  var handleResetPassword = async (record) => {
    props.resetPassword(record.key)
  }

  let renderMeasurementTable = (deviceprogress, e) => {
    // Check if patient has any active devices in careplan
    const activeDevices = e?.deviceschedule?.assigneddevices?.filter(
      (dev) => !dev.isDeleted
    ) || []
    const hasActiveDevices = activeDevices.length > 0

    // Check if patient has any device measurements
    const hasDeviceMeasurements = e?.deviceschedule?.lastscore?.some(
      (score) => score && score.value && (Array.isArray(score.value) ? score.value.some(v => v != null && v != 0) : score.value != null && score.value != 0)
    ) || false

    // Check if patient has any feed scores (historic measurements)
    const hasFeedScores = !!(
      e?.feedschedule?.lastscores?.kneescore != null ||
      e?.feedschedule?.lastscores?.hipscore != null ||
      e?.feedschedule?.lastscores?.extension != null ||
      e?.feedschedule?.lastscores?.flexion != null ||
      e?.feedschedule?.lastscores?.painlevel != null
    )

    // Latest TracPatch temperature reading (femur/tibia), if any.
    const hasTemp = !!(
      e?.lastDayTemp &&
      (e.lastDayTemp.femur != null || e.lastDayTemp.tibia != null)
    )

    // If no devices and no measurements, display NA
    if (!hasActiveDevices && !hasDeviceMeasurements && !hasFeedScores && !hasTemp) {
      return 'NA'
    }

    let objinactive = 0,
      lastdate = ''
    if (
      e.patientactivity?.exerciseactivity?.inactive > 0 &&
      e.patientactivity?.deviceactivity?.inactive > 0 &&
      e.patientactivity?.feedbackactivity?.inactive > 0
    ) {
      objinactive = [
        e.patientactivity?.exerciseactivity,
        e.patientactivity?.deviceactivity,
        e.patientactivity?.feedbackactivity,
      ].sort((a, b) => a.inactive - b.inactive)
      objinactive = objinactive[0]
    }
    console.log('renderMeasurementTable', objinactive, typeof objinactive)

    return (
      <>
        <PatientMeasurementTable
          index={e.key}
          data={e?.deviceschedule}
          feed={e?.feedschedule}
          latestRomTest={e?.latestRomTest}
          lastDaySteps={e?.lastDaySteps}
          lastDayTemp={e?.lastDayTemp}
          companyUser={companyUser}
        />
        {objinactive != 0 && (
          <div
            style={{ color: 'red' }}
          >{`Inactive for ${objinactive.inactive} days ${objinactive.lastactivity ? '- last activity on ' + moment(objinactive.lastactivity).format('MM/DD/YYYY') : ''}`}</div>
        )}
      </>
    )
  }

  // Client-side sort comparators (the full list is in memory).
  const cmpStr = (a, b) =>
    (a || '')
      .toString()
      .toLowerCase()
      .localeCompare((b || '').toString().toLowerCase())
  const cmpDate = (a, b) =>
    (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0)
  // Sort key for the Calibration column. -1 beats every real timestamp, so the
  // patients who have never calibrated successfully lead ascending — which is
  // the order someone chasing calibrations wants.
  //
  // The old MAX_SAFE_INTEGER branch parked "no TracPatch assigned" patients at
  // the far end; that state no longer exists, so never-calibrated and
  // no-record now sort together, which is correct: both need chasing.
  const calibSortKey = (obj) => {
    const c = obj.tracpatchCalibration
    if (!c || !c.calibratedAt) return -1
    return new Date(c.calibratedAt).getTime()
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'lastname',
      width: '170px',
      align: 'center',
      sorter: (a, b) => cmpStr(a.lastname, b.lastname),
      sortDirections: ['ascend', 'descend'],
      render: (lastname, obj) => renderName(lastname, obj),
    },
    {
      title: 'Login Status',
      dataIndex: 'loginStatus',
      align: 'center',
      width: '130px',
      sortDirections: ['ascend', 'descend'],
      // Default view groups Active first, then Away, then Logged Out.
      defaultSortOrder: 'ascend',
      // ascending groups Active first, then Away, then Logged Out
      sorter: (a, b) => {
        const rank = { active: 0, away: 1, loggedout: 2 }
        return (
          (rank[a.loginStatus] ?? 3) - (rank[b.loginStatus] ?? 3)
        )
      },
      render: (loginStatus, obj) => renderLoginStatus(obj),
    },
    {
      title: 'Calibration',
      dataIndex: 'tracpatchCalibration',
      align: 'center',
      width: '120px',
      sortDirections: ['ascend', 'descend'],
      // Ascending surfaces the work: never-calibrated first, then the stalest
      // calibrations, with patients who have no device at all pushed to the end.
      sorter: (a, b) => calibSortKey(a) - calibSortKey(b),
      render: (tracpatchCalibration, obj) => renderCalibration(obj),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      align: 'center',
      sorter: (a, b) => cmpStr(a.provider?.firstname, b.provider?.firstname),
      sortDirections: ['ascend', 'descend'],
      width: '110px',
      render: (provider) => renderSurgeon(provider),
    },
    {
      title: hasAnySurgicalTypes() ? 'Surgery Date' : 'Start Date',
      dataIndex: 'surgery',
      key: 'surgery',
      defaultSortOrder: 'ascend',
      align: 'center',
      sorter: (a, b) => cmpDate(a.surgery?.date, b.surgery?.date),
      sortDirections: ['ascend', 'descend'],
      width: '15%',
      render: (surgery) => rendersurgery(surgery),
    },
    {
      title: 'Measurement Progress',
      dataIndex: 'deviceprogress',
      align: 'center',
      width: 360,
      //sorter: false,
      // render: (deviceprogress, e) => <PatientMeasurement index={e.key} data={e?.deviceschedule} width="250px" companyUser={companyUser}/>
      render: (deviceprogress, e) => renderMeasurementTable(deviceprogress, e),
    },
    {
      title: 'Exercise Progress',
      dataIndex: 'progress',
      align: 'center',
      width: '165px',
      //sorter: true,
      render: (progress, e) =>
        e?.exerciseschedule?.summary ? (
          <PatientPie
            width='250px'
            percentage={e.progress}
            index={e.key}
            data={e?.exerciseschedule?.summary}
          />
        ) : (
          'NA'
        ),
    },
    {
      title: 'Type',
      align: 'center',
      dataIndex: 'surgery',
      key: 'surgerytype',
      sorter: (a, b) => cmpStr(a.surgery?.type, b.surgery?.type),
      sortDirections: ['ascend', 'descend'],
      width: '95px',
      render: (surgery) => rendersurgeryName(surgery),
    },
    /* {
            title: 'Pain Level',
            align: 'center',
            dataIndex: 'painlevel',
            sorter: true,
            render: (row, obj) => obj?.feedschedule?.lastscores?.painlevel ? 
                obj?.feedschedule?.lastscores?.painlevel : ' NA '
        },
        {
            title: 'Score',
            dataIndex: 'kneehipscore',
            sorter: true,
            align: 'center',
            render: (row, obj) => obj?.feedschedule?.lastscores?.kneescore
                ? obj?.feedschedule?.lastscores?.kneescore
                : obj?.feedschedule?.lastscores?.hipscore
                    ? obj?.feedschedule?.lastscores?.hipscore
                    : ' NA '               
        },
        {
            title: 'Knee Score',
            dataIndex: 'kneescore',
            sorter: true,
            align: 'center',
            render: (row, obj) => obj?.feedschedule?.lastscores?.kneescore ? 
                obj?.feedschedule?.lastscores?.kneescore : ' NA '
        },
        {
            title: 'Hip Score',
            dataIndex: 'hipscore',
            sorter: true,
            align: 'center',
            render: (row, obj) => obj?.feedschedule?.lastscores?.hipscore ? 
                obj?.feedschedule?.lastscores?.hipscore : ' NA '
        },*/
    {
      title: 'Action',
      align: 'center',
      // width: '155px',
      width: '120px',
      render: (row) => renderActions(row),
    },
  ]

  const components = {
    table: (props) => <table {...props} />,
    header: {
      wrapper: (props) => <thead id='patientlist' {...props} />,
    },
  }

  return (
    <>
      <Table
        size={'large'}
        className={'custom-table'}
        columns={columns}
        dataSource={dataSource}
        loading={props.isLoading}
        pagination={pagination}
        rowSelection={rowSelection}
        bordered={false}
        onRow={handleRow}
        components={components}
        showSorterTooltip={{ title: 'Click to sort' }}
        locale={{
          // An empty table that is empty only because of a filter must SAY so.
          // Antd's default "No Data" is indistinguishable from "this clinic has
          // no patients", which on 2026-10-28 read as two newly registered
          // patients having vanished -- they were simply in the other tab,
          // because self-registration without a known surgery date transmits a
          // date a year out.
          emptyText:
            hiddenByScheduleView > 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 15, color: '#012448', marginBottom: 6 }}>
                  {`No patients here, but ${hiddenByScheduleView} ${
                    hiddenByScheduleView === 1 ? 'patient is' : 'patients are'
                  } hidden by the Surgery Date filter.`}
                </div>
                <div style={{ fontSize: 13, color: '#8a8a8a', marginBottom: 12 }}>
                  Patients who registered without a known surgery date appear
                  under <strong>Not Scheduled</strong>.
                </div>
                <Button
                  type='primary'
                  onClick={() => props.setScheduleView('all')}
                >
                  Show All
                </Button>
              </div>
            ) : undefined,
        }}
      />
      <PatientMail mail={sendmail} onCancel={() => setSendmail('')} />
      <CaseModal
              visible={caseOpen}
              onClose={() => setCaseOpen(false)}
              onSuccess={handleSuccess}
              record={singleRecord}
              moveai
      />
    </>
  )
}

PatientTable.propTypes = {
  fetchPatient: PropTypes.func.isRequired,
  fetchSurgeon: PropTypes.func.isRequired,
  fetchFeedbackForms: PropTypes.func.isRequired,
  onRowSelect: PropTypes.func.isRequired,
  getReport: PropTypes.func.isRequired,
  customer: PropTypes.object,
  pagination: PropTypes.object,
  patients: PropTypes.array,
  isLoading: PropTypes.bool,
  filters: PropTypes.object,
  surgeon: PropTypes.array,
  forms: PropTypes.array,
  selectedPatients: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    customer: state.profile.customer,
    pagination: state.patient.metadata,
    patients: state.patient.patients,
    isLoading: state.patient.isLoading,
    filters: state.patient.filters,
    search: state.patient.search,
    scheduleView: state.patient.scheduleView,
    surgeon: state.surgeon.surgeons,
    forms: state.feedback.forms,
    selectedPatients: state.patient.selectedPatients,
    decoded: util.verifyToken(state.profile.token, true),
    user: state.profile?.user,
    role: state.profile.user.role,
    defaultFilter: state.profile.user.filter,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchPatient: (params, patients, silent) =>
      dispatch(getPatientList(params, patients, silent)),
    fetchSurgeon: (customerid) => dispatch(getSurgeonList(customerid)),
    fetchFeedbackForms: (customerid) => dispatch(getFeedbackForms(customerid)),
    onRowSelect: (rows) => dispatch(rowSelect(rows)),
    getReport: (id) => dispatch(getReport(id)),
    getStats: (customerid) => dispatch(getStats(customerid)),
    resetPassword: (patientid) => dispatch(setPatientPassword(patientid)),
    messageGroupBy: (data) => dispatch(messageGroupBy(data)),
    setPatient: (patient) => dispatch(setPatient(patient)),
    getNotifications: (id) => dispatch(getPatientNots(id)),
    // Lets the empty state switch the Surgery Date view, so the fix for an
    // unexpectedly empty list is one click rather than knowing where to look.
    setScheduleView: (view) => dispatch(setPatientScheduleView(view)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientTable)
